import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, BookOpen, Search, Code, FileText } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { Subject, SubjectFormData } from '../../types';
import { subjectsApi } from '../../services/api';
import { ConfirmDialog } from '../ui';

const subjectSchema = yup.object({
  name: yup
    .string()
    .required('Subject name is required')
    .min(2, 'Subject name must be at least 2 characters')
    .max(100, 'Subject name must not exceed 100 characters'),
  code: yup
    .string()
    .required('Subject code is required')
    .min(2, 'Subject code must be at least 2 characters')
    .max(10, 'Subject code must not exceed 10 characters')
    .matches(/^[A-Z0-9]+$/, 'Subject code must contain only uppercase letters and numbers'),
  description: yup
    .string()
    .optional()
    .max(500, 'Description must not exceed 500 characters'),
  isActive: yup
    .boolean()
    .required()
}).required();

const SubjectManagement: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<SubjectFormData>({
    resolver: yupResolver(subjectSchema) as any,
    defaultValues: {
      name: '',
      code: '',
      description: '',
      isActive: true
    }
  });

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      const subjectsData = await subjectsApi.getAll();
      setSubjects(subjectsData);
    } catch (error) {
      toast.error('Failed to load subjects');
      console.error('Error loading subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubject = async (data: any) => {
    try {
      setSubmitting(true);
      const formData: SubjectFormData = data as SubjectFormData;
      const newSubject = await subjectsApi.create(formData);
      setSubjects(prev => [newSubject, ...prev]);
      setShowForm(false);
      reset();
      toast.success('Subject created successfully');
    } catch (error) {
      toast.error('Failed to create subject');
      console.error('Error creating subject:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubject = async (data: any) => {
    if (!editingSubject) return;

    try {
      setSubmitting(true);
      const formData: SubjectFormData = data as SubjectFormData;
      await subjectsApi.update(editingSubject.id, formData);
      setSubjects(prev =>
        prev.map(subject =>
          subject.id === editingSubject.id
            ? {
                ...subject,
                ...formData,
                updatedAt: new Date().toISOString()
              }
            : subject
        )
      );
      setEditingSubject(null);
      setShowForm(false);
      reset();
      toast.success('Subject updated successfully');
    } catch (error) {
      toast.error('Failed to update subject');
      console.error('Error updating subject:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubject = (subject: Subject) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Subject',
      message: `Are you sure you want to delete "${subject.name}" (${subject.code})?\n\nThis action cannot be undone and will remove the subject permanently.`,
      onConfirm: async () => {
        try {
          await subjectsApi.delete(subject.id);
          setSubjects(prev => prev.filter(s => s.id !== subject.id));
          toast.success('Subject deleted successfully');
        } catch (error) {
          toast.error('Failed to delete subject');
          console.error('Error deleting subject:', error);
        }
      }
    });
  };

  const handleEdit = (subject: Subject) => {
    setEditingSubject(subject);
    reset({
      name: subject.name,
      code: subject.code,
      description: subject.description || '',
      isActive: subject.isActive
    });
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingSubject(null);
    reset();
  };

  const filteredSubjects = subjects.filter(subject => {
    const matchesSearch =
      subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (subject.description && subject.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus =
      !filterStatus ||
      (filterStatus === 'active' && subject.isActive) ||
      (filterStatus === 'inactive' && !subject.isActive);

    return matchesSearch && matchesStatus;
  });

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-[#f3ede2]">
              {editingSubject ? 'Edit Subject' : 'Create New Subject'}
            </h3>
            <p className="text-[#b9ad9b] text-sm mt-1">
              {editingSubject ? 'Update subject details' : 'Add a new subject to the curriculum'}
            </p>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
          <form onSubmit={handleSubmit(editingSubject ? handleEditSubject : handleCreateSubject)} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Subject Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  placeholder="Enter subject name (e.g., Mathematics, Physics)"
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                />
                {errors.name && (
                  <p className="text-[#f38b83] text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                  Subject Code *
                </label>
                <input
                  type="text"
                  {...register('code')}
                  placeholder="Enter subject code (e.g., MATH, PHY)"
                  style={{ textTransform: 'uppercase' }}
                  className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                />
                {errors.code && (
                  <p className="text-[#f38b83] text-sm mt-1">{errors.code.message}</p>
                )}
                <p className="text-[#8a7f6e] text-xs mt-1">
                  Use uppercase letters and numbers only
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                placeholder="Enter subject description (optional)"
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent resize-vertical"
              />
              {errors.description && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.description.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('isActive')}
                id="isActive"
                className="w-4 h-4 text-[#e2916d] border-[#3a3122] rounded focus:ring-[#e2916d]"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-[#b9ad9b]">
                Active
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-[#3a3122]">
              <button
                type="button"
                onClick={handleCancelForm}
                className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {submitting ? 'Saving...' : editingSubject ? 'Update' : 'Create'} Subject
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2]">Subject Management</h3>
          <p className="text-[#b9ad9b] text-sm mt-1">Manage school subjects and curriculum</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Subject</span>
        </button>
      </div>

      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="p-6 border-b border-[#3a3122]">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8a7f6e]" />
              <input
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent w-full"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <p className="text-[#b9ad9b] text-lg">
              {subjects.length === 0 ? 'No subjects found' : 'No subjects match your search'}
            </p>
            <p className="text-[#8a7f6e] text-sm">
              {subjects.length === 0 ? 'Create your first subject to get started' : 'Try adjusting your search criteria'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#3a3122]">
            {filteredSubjects.map((subject) => (
              <div key={subject.id} className="p-6 hover:bg-[#28221a] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#173322] rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-[#4ade80]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-[#f3ede2]">{subject.name}</h4>
                        <div className="flex items-center space-x-1">
                          <Code className="w-3 h-3 text-[#8a7f6e]" />
                          <span className="px-2 py-1 bg-[#16212b] text-[#bfdbfe] rounded text-xs font-mono">
                            {subject.code}
                          </span>
                        </div>
                        {!subject.isActive && (
                          <span className="px-2 py-1 bg-[#b9ad9b] text-[#5c5346] rounded-full text-xs">
                            Inactive
                          </span>
                        )}
                      </div>
                      {subject.description && (
                        <div className="flex items-start space-x-2 mt-2">
                          <FileText className="w-4 h-4 text-[#8a7f6e] mt-0.5 flex-shrink-0" />
                          <p className="text-[#b9ad9b] text-sm line-clamp-2">{subject.description}</p>
                        </div>
                      )}
                      <div className="text-xs text-[#8a7f6e] mt-2">
                        Created: {new Date(subject.createdAt).toLocaleDateString()}
                        {subject.updatedAt !== subject.createdAt && (
                          <span> • Updated: {new Date(subject.updatedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(subject)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711] rounded-lg transition-colors"
                      title="Edit Subject"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSubject(subject)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                      title="Delete Subject"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
};

export default SubjectManagement;